import { useCallback, useEffect, useState } from 'react';
import { apiV2, CARRIERS, carrierLabel, type Friend, type FriendDraft, type FriendGroup } from '../lib/apiV2';
import { isSignedIn } from '../lib/auth';
import { MAX_INVITEES } from '../../shared/protocol';
import { AuthSheet } from './AuthSheet';

export function emptyFriendDraft(): FriendDraft {
  return { name: '', email: '', phone: '', carrierGateway: null, attendMode: 'browser', favorite: false };
}

/**
 * The add/edit friend form — shared by the Friends screen and the create
 * flow's inline "+ New friend" sheet. Carrier picker stores a gateway DOMAIN
 * (or null = email only; we never guess a gateway).
 */
export function FriendForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
}: {
  initial: FriendDraft;
  submitLabel: string;
  onSubmit: (d: FriendDraft) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [d, setD] = useState<FriendDraft>(initial);
  const [warn, setWarn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nagged, setNagged] = useState(false);

  const set = (patch: Partial<FriendDraft>) => setD((prev) => ({ ...prev, ...patch }));

  const submit = () => {
    const name = d.name.trim();
    if (!name) {
      setError('A name is the only must-have.');
      return;
    }
    const phone = (d.phone ?? '').replace(/\D/g, '');
    if (phone && phone.length !== 10) {
      setError('Phone should be 10 digits (or leave it empty).');
      return;
    }
    // phone but no carrier: nag ONCE, then save fine (no SMS attempted)
    if (phone && !d.carrierGateway && !nagged) {
      setNagged(true);
      setWarn('No carrier picked — they’ll get email only, no texts. Save again if that’s fine.');
      return;
    }
    setError(null);
    onSubmit({
      ...d,
      name,
      email: d.email?.trim() || undefined,
      phone: phone || undefined,
    });
  };

  return (
    <div className="friend-form">
      <label>Name</label>
      <input
        type="text"
        value={d.name}
        maxLength={32}
        placeholder="Friend’s name"
        onChange={(e) => set({ name: e.target.value })}
        autoFocus
      />
      <div className="row">
        <div>
          <label>
            Email <span className="hint">(for invites)</span>
          </label>
          <input
            type="email"
            inputMode="email"
            value={d.email ?? ''}
            placeholder="them@example.com"
            onChange={(e) => set({ email: e.target.value })}
          />
        </div>
        <div>
          <label>
            Phone <span className="hint">(texts, best-effort)</span>
          </label>
          <input
            type="tel"
            inputMode="tel"
            value={d.phone ?? ''}
            placeholder="10 digits"
            maxLength={14}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </div>
      </div>
      <label>Carrier</label>
      <select
        value={d.carrierGateway ?? ''}
        onChange={(e) => {
          setWarn(null);
          set({ carrierGateway: e.target.value || null });
        }}
      >
        {CARRIERS.map((c) => (
          <option key={c.label} value={c.gateway ?? ''}>
            {c.label}
          </option>
        ))}
      </select>
      <div className="toggle-row">
        <button
          type="button"
          className={`chip toggle${d.attendMode === 'vr' ? ' on' : ''}`}
          onClick={() => set({ attendMode: d.attendMode === 'vr' ? 'browser' : 'vr' })}
          aria-pressed={d.attendMode === 'vr'}
        >
          🥽 Has a VR headset
        </button>
        <button
          type="button"
          className={`chip toggle${d.favorite ? ' on' : ''}`}
          onClick={() => set({ favorite: !d.favorite })}
          aria-pressed={d.favorite}
          title="Favorites are pre-invited on every new night"
        >
          ★ Favorite
        </button>
      </div>
      <p className="hint-line">Favorites are pre-invited on every new night.</p>
      {warn && <div className="notice warn">{warn}</div>}
      {error && <div className="notice error">{error}</div>}
      <div className="form-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>
          {busy ? <span className="spinner" /> : submitLabel}
        </button>
        <button className="btn link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function friendBadges(f: {
  email?: string | null;
  phone?: string | null;
  carrierGateway?: string | null;
  attendMode?: 'vr' | 'browser';
}): string {
  const parts: string[] = [];
  if (f.email) parts.push('✉');
  if (f.phone && f.carrierGateway) parts.push('📱');
  parts.push(f.attendMode === 'vr' ? '🥽' : '🖥');
  return parts.join(' ');
}

/** Name + membership form for one group. Membership caps at MAX_INVITEES: a
 * group too big to invite in one tap would defeat the entire point of groups. */
function GroupForm({
  friends,
  initialName,
  initialMemberIds,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
}: {
  friends: Friend[];
  initialName: string;
  initialMemberIds: string[];
  submitLabel: string;
  onSubmit: (name: string, memberIds: string[]) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [ids, setIds] = useState<Set<string>>(() => new Set(initialMemberIds));
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    const n = name.trim();
    if (!n) {
      setError('Give the group a name.');
      return;
    }
    if (ids.size === 0) {
      setError('Pick at least one friend.');
      return;
    }
    if (ids.size > MAX_INVITEES) {
      setError(`A night takes up to ${MAX_INVITEES} people — trim the group to match.`);
      return;
    }
    setError(null);
    onSubmit(n, [...ids]);
  };

  return (
    <div className="friend-form">
      <label>Group name</label>
      <input
        type="text"
        value={name}
        maxLength={32}
        placeholder="VR crew"
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <label>
        Who’s in it{' '}
        <span className="hint">
          ({ids.size}/{MAX_INVITEES})
        </span>
      </label>
      {friends.length === 0 ? (
        <div className="empty-state">Save some friends first — a group is a shortcut to them.</div>
      ) : (
        <div className="friend-list">
          {friends.map((f) => (
            <button
              type="button"
              key={f.id}
              className={`chip toggle${ids.has(f.id) ? ' on' : ''}`}
              onClick={() => toggle(f.id)}
              aria-pressed={ids.has(f.id)}
            >
              {ids.has(f.id) ? '✓ ' : ''}
              {f.name}
            </button>
          ))}
        </div>
      )}
      {error && <div className="notice error">{error}</div>}
      <div className="form-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>
          {busy ? <span className="spinner" /> : submitLabel}
        </button>
        <button className="btn link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Named groups — a saved selection the create flow invites in one tap. Groups
 * are purely a selection shortcut: creating a night expands one into friend ids
 * and rides the existing invite path, so nothing in the send / snapshot /
 * reminder machinery knows groups exist.
 */
export function FriendGroupsCard({ friends }: { friends: Friend[] }) {
  const [groups, setGroups] = useState<FriendGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FriendGroup | 'new' | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setGroups(await apiV2.listGroups());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your groups.');
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = async (name: string, memberIds: string[]) => {
    setBusy(true);
    try {
      if (editing && editing !== 'new') await apiV2.updateGroup(editing.id, { name, memberIds });
      else await apiV2.addGroup(name, memberIds);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the group.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (g: FriendGroup) => {
    if (!window.confirm(`Delete the “${g.name}” group? Your friends stay saved.`)) return;
    try {
      await apiV2.removeGroup(g.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the group.');
    }
  };

  const memberNames = (g: FriendGroup): string =>
    g.memberIds
      .map((id) => friends.find((f) => f.id === id)?.name)
      .filter((n): n is string => Boolean(n))
      .join(', ');

  return (
    <div className="card marquee">
      <div className="flex-between">
        <h2 className="display">Groups</h2>
        <button className="btn primary small" onClick={() => setEditing('new')}>
          + New group
        </button>
      </div>
      <p className="soft">A saved set of friends you invite in one tap.</p>

      {error && <div className="notice error">{error}</div>}

      {groups === null ? (
        <div className="center">
          <span className="spinner" /> Loading…
        </div>
      ) : groups.length === 0 && editing === null ? (
        <div className="empty-state">
          No groups yet. Make one — “VR crew”, “the regulars” — and inviting them becomes one tap.
        </div>
      ) : (
        <div className="friend-list">
          {groups.map((g) => (
            <div className="friend-row" key={g.id}>
              <div className="friend-info">
                <div className="name">{g.name}</div>
                <div className="sub">
                  {g.memberIds.length} {g.memberIds.length === 1 ? 'person' : 'people'}
                  {memberNames(g) ? ` · ${memberNames(g)}` : ''}
                </div>
              </div>
              <button
                className="btn secondary small"
                onClick={() => setEditing(g)}
                aria-label={`Edit ${g.name}`}
              >
                ✏️ Edit
              </button>
              <button className="remove-x" onClick={() => remove(g)} aria-label={`Delete ${g.name}`}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="sheet-backdrop" role="dialog" aria-modal="true">
          <div className="sheet">
            <div className="card marquee">
              <h2 className="display">{editing === 'new' ? 'New group' : `Edit ${editing.name}`}</h2>
              <GroupForm
                friends={friends}
                initialName={editing === 'new' ? '' : editing.name}
                initialMemberIds={editing === 'new' ? [] : editing.memberIds}
                submitLabel={editing === 'new' ? 'Save group' : 'Save changes'}
                busy={busy}
                onSubmit={save}
                onCancel={() => setEditing(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The signed-in Friends screen (?tab=friends): list + add/edit sheet. */
export function FriendsManager() {
  const [signedIn] = useState(isSignedIn());
  const [friends, setFriends] = useState<Friend[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Friend | 'new' | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await apiV2.listFriends();
      list.sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name));
      setFriends(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your friends.');
      setFriends([]);
    }
  }, []);

  useEffect(() => {
    if (signedIn) refresh();
  }, [signedIn, refresh]);

  if (!signedIn) {
    return <AuthSheet title="Sign in for saved friends" />;
  }

  const save = async (draft: FriendDraft) => {
    setBusy(true);
    try {
      if (editing && editing !== 'new') await apiV2.updateFriend(editing.id, draft);
      else await apiV2.addFriend(draft);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const toggleFavorite = async (f: Friend) => {
    try {
      await apiV2.updateFriend(f.id, {
        name: f.name,
        email: f.email ?? undefined,
        phone: f.phone ?? undefined,
        carrierGateway: f.carrierGateway ?? null,
        attendMode: f.attendMode,
        favorite: !f.favorite,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update.');
    }
  };

  const remove = async (f: Friend) => {
    if (!window.confirm(`Remove ${f.name}? They stop getting invites.`)) return;
    try {
      await apiV2.removeFriend(f.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove.');
    }
  };

  return (
    <div>
      <div className="card marquee">
        <div className="flex-between">
          <h2 className="display">Friends</h2>
          <button className="btn primary small" onClick={() => setEditing('new')}>
            + Add a friend
          </button>
        </div>
        <p className="soft">Saved once — favorites are pre-invited on every new night.</p>

        {error && <div className="notice error">{error}</div>}

        {friends === null ? (
          <div className="center">
            <span className="spinner" /> Loading…
          </div>
        ) : friends.length === 0 && editing === null ? (
          <div className="empty-state">
            No friends saved yet. Add them once — invites go out automatically every night after.
          </div>
        ) : (
          <div className="friend-list">
            {friends.map((f) => (
              <div className="friend-row" key={f.id}>
                <button
                  className={`star${f.favorite ? ' on' : ''}`}
                  onClick={() => toggleFavorite(f)}
                  title={f.favorite ? 'Remove from favorites' : 'Favorites are pre-invited'}
                  aria-label={`${f.favorite ? 'Unfavorite' : 'Favorite'} ${f.name}`}
                >
                  ★
                </button>
                <div className="friend-info">
                  <div className="name">{f.name}</div>
                  <div className="sub">
                    {friendBadges(f)}
                    {f.phone && !f.carrierGateway ? ' · no carrier (email only)' : ''}
                    {!f.email && !f.phone ? ' · no contact info' : ''}
                    {f.carrierGateway ? ` · ${carrierLabel(f.carrierGateway)}` : ''}
                  </div>
                </div>
                <button
                  className="btn secondary small"
                  onClick={() => setEditing(f)}
                  aria-label={`Edit ${f.name}`}
                >
                  ✏️ Edit
                </button>
                <button className="remove-x" onClick={() => remove(f)} aria-label={`Remove ${f.name}`}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <FriendGroupsCard friends={friends ?? []} />

      {editing !== null && (
        <div className="sheet-backdrop" role="dialog" aria-modal="true">
          <div className="sheet">
            <div className="card marquee">
              <h2 className="display">{editing === 'new' ? 'New friend' : `Edit ${editing.name}`}</h2>
              <FriendForm
                initial={
                  editing === 'new'
                    ? emptyFriendDraft()
                    : {
                        name: editing.name,
                        email: editing.email ?? '',
                        phone: editing.phone ?? '',
                        carrierGateway: editing.carrierGateway ?? null,
                        attendMode: editing.attendMode,
                        favorite: editing.favorite,
                      }
                }
                submitLabel={editing === 'new' ? 'Save friend' : 'Save changes'}
                busy={busy}
                onSubmit={save}
                onCancel={() => setEditing(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
